import type { Challenge } from './definition';
import { fixedCells } from './editing';

export type CountingVersion = 1 | 2 | 3;
/** Derive current play without mutating the source. Explicit author saves may export
 * this profile as format7; existing attempts/publications retain their original definition. */
export function currentPlayChallenge(challenge: Challenge): Challenge {
  if (challenge.countingVersion === 3)
    return {
      ...challenge,
      scoring: challenge.scoring
        ? { ...challenge.scoring, metricsVersion: 3 }
        : undefined,
    };
  return {
    ...challenge,
    countingVersion: 2,
    ...(challenge.scoring
      ? { scoring: { ...challenge.scoring, metricsVersion: 2 } }
      : {}),
  };
}

/** Omitted versions retain the exact meaning of historical definitions/results. */
export function countingVersion(
  challenge: Pick<Challenge, 'countingVersion' | 'scoring'>,
): CountingVersion {
  return challenge.countingVersion ?? challenge.scoring?.metricsVersion ?? 1;
}

interface PlacedInstance {
  id: string;
  chipId: string;
  originPointDto?: { x: number; y: number };
  rotation?: number;
}
interface PlacedTile {
  tileType: string;
  meta: { partOfChip?: string | null };
  globalTileLocation: { location: { x: number; y: number } };
}
/** Match authored fixed placements, never a client-provided fixed flag. Only root
 * placements are exempt; a player's copy of the same definition is fully charged.
 * Consume each fixed slot once so duplicate/spoofed instances cannot all be free. */
export function playerConstruction<T extends PlacedTile, I extends PlacedInstance>(
  tiles: readonly T[],
  instances: readonly I[],
  challenge: Challenge,
): { tiles: readonly T[]; instances: readonly I[] } {
  if (countingVersion(challenge) === 1) return { tiles, instances };
  const remaining = (challenge.starterChips ?? []).filter((p) => p.fixed);
  const fixedIds = new Set<string>();
  for (const instance of instances) {
    const index = remaining.findIndex(
      (p) =>
        p.chipId === instance.chipId &&
        p.x === instance.originPointDto?.x &&
        p.y === instance.originPointDto?.y &&
        (p.rotation ?? 0) === (instance.rotation ?? 0),
    );
    if (index < 0) continue;
    fixedIds.add(instance.id);
    remaining.splice(index, 1);
  }
  const fixed = new Set(fixedCells(challenge).map((p) => `${p.type}:${p.x},${p.y}`));
  return {
    tiles: tiles.filter((tile) => {
      if (tile.meta.partOfChip != null) return !fixedIds.has(tile.meta.partOfChip);
      const { x, y } = tile.globalTileLocation.location;
      return !fixed.has(`${tile.tileType}:${x},${y}`);
    }),
    instances: instances.filter((instance) => !fixedIds.has(instance.id)),
  };
}
